import type { JSX } from 'react';
import { useState } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { VerificationStatusBadge } from './VerificationStatusBadge';
import { VerificationActions } from './VerificationActions';
import { ReturnedComment } from './ReturnedComment';
import { RefereeResponse, RefereeResponseToggle } from './RefereeResponse';
import { hasRefereeResponse } from '../utils/refereeResponse';
import type { Verification } from '../types/verification.types';

interface VerificationTableRowProps {
  verification: Verification;
  onDelete: () => void;
  onCopyRefereeLink: () => void;
}

/** Owns its own expanded state so the table stays a plain list of rows. */
export function VerificationTableRow({
  verification,
  onDelete,
  onCopyRefereeLink,
}: VerificationTableRowProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const showResponse = hasRefereeResponse(verification);

  return (
    <>
      <TableRow>
        <TableCell className="font-medium">{verification.refereeName}</TableCell>
        <TableCell>{verification.refereeEmail}</TableCell>
        <TableCell>{verification.refereeJobTitle ?? '-'}</TableCell>
        <TableCell>
          <VerificationStatusBadge status={verification.status} />
          <ReturnedComment
            status={verification.status}
            comment={verification.inspectionComment}
            className="mt-1 max-w-xs truncate"
          />
          {showResponse && (
            <RefereeResponseToggle
              isExpanded={isExpanded}
              onToggle={() => setIsExpanded((expanded) => !expanded)}
            />
          )}
        </TableCell>
        <TableCell className="text-right">
          <VerificationActions onDelete={onDelete} onCopyRefereeLink={onCopyRefereeLink} />
        </TableCell>
      </TableRow>
      {showResponse && isExpanded && (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/40 whitespace-normal break-words">
            <RefereeResponse verification={verification} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
